import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box, Stack, Typography, TextField, MenuItem, Button, Alert, Paper,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  IconButton, Tooltip, CircularProgress, FormControlLabel, Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { BRAND } from "../styles/theme";
import { CollectorSummaryRow, getCollectorSummary, rebuildCollectorSummary } from "../services/collectorStats";
import { listCollectorsWithContract } from "../services/contracts";
import { ProcessorChip, BoolChip, fmtDateTime } from "../components/collectors/chips";
import NewCollectorDialog from "../components/collectors/NewCollectorDialog";

type StatusFilter = "all" | "active" | "inactive";
type ContractFilter = "all" | "yes" | "no";

/**
 * Collector Management — lista raíz. Reemplaza `MenuConciProductsList` de v1:
 * los toggles progresivos All/Active/Settlement pasan a select + switch siempre
 * visibles y el estado vive en la URL (?q=&status=&settle=1) para poder
 * compartir links y que el back del browser funcione.
 *
 * La columna Contract cruza con GET /collectors/contracts (lambda `contracts`):
 * quién tiene `contract_html` en collection_config. Si esa llamada falla, la
 * lista sigue andando sin la columna.
 *
 * Los datos salen de `webadmin2_collector_stats` (GET /collectors/summary): una
 * lectura con processor/close/active/settlement + cuentas y usuarios por
 * collector, mantenida por streams de las tablas core. Refresh = rebuild
 * completo (~10k RCU) y polling hasta que cambie `rebuilt_at`.
 */
export default function Collectors() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const q      = sp.get("q") ?? "";
  const status = (sp.get("status") as StatusFilter) || "all";
  const settle = sp.get("settle") === "1";
  const contractF = (sp.get("contract") as ContractFilter) || "all";
  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(sp);
    if (v) next.set(k, v); else next.delete(k);
    setSp(next, { replace: true });
  };

  const [rows, setRows]       = useState<CollectorSummaryRow[]>([]);
  const [rebuiltAt, setRebuiltAt] = useState<string | null>(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState<string | null>(null);
  const [ok, setOk]           = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  // null = todavía no se sabe (o falló): la columna muestra "…"/"—" y el filtro se desactiva.
  const [withContract, setWithContract] = useState<Set<string> | null>(null);
  const [contractErr, setContractErr]   = useState(false);

  async function load(silent = false) {
    if (!silent) setLoading(true);
    setErr(null);
    try {
      const s = await getCollectorSummary();
      setRows(s.items); setRebuiltAt(s.rebuilt_at);
      return s;
    } catch (e: any) { setErr(String(e?.message ?? e)); return null; }
    finally { if (!silent) setLoading(false); }
  }
  async function loadContracts(refresh = false) {
    setContractErr(false);
    try { setWithContract(await listCollectorsWithContract(refresh)); }
    catch { setContractErr(true); }
  }
  useEffect(() => { void load(); void loadContracts(); }, []);

  // Refresh = recalcular la tabla completa (async en la lambda) y esperar el
  // nuevo rebuilt_at. Patrón de Settlements (sync DEBINes): 3 s, tope 5 min.
  async function onRefresh() {
    setRebuilding(true); setErr(null); setOk(null);
    try {
      const { triggered, prev_rebuilt_at, reason } = await rebuildCollectorSummary();
      if (!triggered && reason && !/running/i.test(reason)) { setErr(reason); return; }
      const deadline = Date.now() + 300_000;
      let changed = false;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const s = await load(true);
        if (s && (s.rebuilt_at ?? "") !== (prev_rebuilt_at ?? "") && !s.rebuilding) { changed = true; break; }
      }
      if (changed) setOk("Collector stats recalculated.");
      else setErr("The rebuild is still running — the table will update on its own shortly.");
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setRebuilding(false); }
  }

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows
      .filter((c) => {
        if (status === "active" && !c.active) return false;
        if (status === "inactive" && c.active) return false;
        if (settle && !c.settlement) return false;
        if (withContract && contractF !== "all" && withContract.has(c.id) !== (contractF === "yes")) return false;
        if (!s) return true;
        return [c.id, c.payments_processor, c.close_id ?? ""]
          .some((v) => v.toLowerCase().includes(s));
      })
      .sort((a, b) => a.id.localeCompare(b.id, undefined, { sensitivity: "base" }));
  }, [rows, q, status, settle, contractF, withContract]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Collectors</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Every collector on the platform. Open one to manage its configuration, accounts, users and onboarding.
      </Typography>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <TextField size="small" label="Search" placeholder="Id, processor, close…" value={q}
          onChange={(e) => setParam("q", e.target.value)} sx={{ width: 320 }} />
        <TextField size="small" select label="Status" value={status}
          onChange={(e) => setParam("status", e.target.value === "all" ? "" : e.target.value)} sx={{ width: 160 }}>
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
        <TextField size="small" select label="Contract" value={contractF} disabled={!withContract}
          onChange={(e) => setParam("contract", e.target.value === "all" ? "" : e.target.value)} sx={{ width: 160 }}>
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="yes">With contract</MenuItem>
          <MenuItem value="no">Without contract</MenuItem>
        </TextField>
        <FormControlLabel control={<Switch checked={settle} onChange={(e) => setParam("settle", e.target.checked ? "1" : "")} />}
          label="Settlement account enabled" />
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewOpen(true)} sx={{ height: 40 }}>
          New collector
        </Button>
      </Stack>

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}
      {contractErr && (
        <Alert severity="warning" sx={{ mb: 2 }} action={<Button size="small" onClick={() => loadContracts(true)}>Retry</Button>}>
          Couldn't load which collectors have a contract — the Contract column and filter are unavailable.
        </Alert>
      )}
      {ok  && <Alert severity="success" onClose={() => setOk(null)} sx={{ mb: 2 }}>{ok}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead sx={{ bgcolor: BRAND.primaryLight }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Id</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Processor</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Close</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Settlement acct</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Contract</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Accounts <Typography component="span" variant="caption" color="text.secondary">(active/total)</Typography></TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Users</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: "text.secondary" }}>
                {rows.length === 0 ? "No collectors." : <>No collectors match. <Button size="small" onClick={() => setSp({}, { replace: true })}>Clear filters</Button></>}
              </TableCell></TableRow>
            ) : filtered.map((c) => (
              <TableRow key={c.id} hover sx={{ cursor: "pointer", ...(c.active ? {} : { "& td": { opacity: 0.55 } }) }}
                onClick={() => nav(`/collector/${encodeURIComponent(c.id)}`)}>
                <TableCell sx={{ fontWeight: 700 }}>{c.id}</TableCell>
                <TableCell><ProcessorChip value={c.payments_processor} /></TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{c.close_id || <span style={{ color: "#888" }}>—</span>}</TableCell>
                <TableCell><BoolChip value={c.active} yes="Active" no="Inactive" /></TableCell>
                <TableCell><BoolChip value={c.settlement} yes="Enabled" no="Disabled" /></TableCell>
                <TableCell>{withContract
                  ? <BoolChip value={withContract.has(c.id)} yes="Yes" no="Missing" />
                  : <span style={{ color: "#888" }}>{contractErr ? "—" : "…"}</span>}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {/* CP-442: "accounts" incluye cuentas deleted=yes (ej. migradas Bind->CMF), lo que */}
                  {/* duplicaba casi exacto el número — se muestran ambos en vez de cambiar el significado. */}
                  <Tooltip title={`${c.accounts_active.toLocaleString("en")} active, ${c.accounts.toLocaleString("en")} total (includes disabled/migrated accounts)`}>
                    <span>{c.accounts_active.toLocaleString("en")}/{c.accounts.toLocaleString("en")}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{c.users}</TableCell>
                <TableCell align="right" sx={{ opacity: 1 }}>
                  <Tooltip title="Open"><IconButton size="small"><ChevronRightIcon /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1, borderTop: 1, borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary">{filtered.length} of {rows.length} collectors</Typography>
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {rebuilding ? "Recalculating…" : rebuiltAt ? `Stats updated ${fmtDateTime(rebuiltAt)}` : "Stats never calculated"}
          </Typography>
          <Tooltip title="Recalculate accounts and users for every collector"><span>
            <IconButton size="small" onClick={onRefresh} disabled={loading || rebuilding}>
              {rebuilding ? <CircularProgress size={18} /> : <RefreshIcon />}
            </IconButton></span></Tooltip>
        </Stack>
      </TableContainer>

      <NewCollectorDialog open={newOpen} existingIds={rows.map((r) => r.id)} onClose={() => setNewOpen(false)}
        onCreated={(id) => { setNewOpen(false); nav(`/collector/${encodeURIComponent(id)}/config?new=1`); }} />

    </Box>
  );
}
